/**
 * The locale registry — one source of truth for every language the CRM speaks.
 *
 * Before this file the locale list existed three times and disagreed with
 * itself: the switcher hardcoded ['de','en','sr'], the settings panel
 * hardcoded the same list again, and SUPPORTED_LOCALES in enums.ts was
 * imported by nothing. Meanwhile `clients.preferred_language` accepted a
 * different set of ten, and the PDF renderer a third. Adding a language meant
 * finding all of them.
 *
 * `status` is what makes the registry safe to grow: all the languages the owner
 * asked for are declared here, but only the ones marked 'live' have a
 * translation file and are selectable. Promoting a language is a one-word edit
 * once its JSON lands — and apps/web/src/i18n/locales.test.ts fails if the two
 * ever disagree in either direction.
 *
 * `intl` is deliberately separate from `code`. The i18next resource code stays
 * bare ('sr', 'fa') so `load: 'languageOnly'` keeps working and no stored user
 * preference needs migrating; the rich BCP-47 tag is used only for Intl.*
 * date, number and currency formatting.
 */

export type LocaleScript =
  | 'latin'
  | 'cyrillic'
  | 'greek'
  | 'arabic'
  | 'hebrew'
  | 'devanagari'
  | 'han'
  | 'hangul';

export interface LocaleMeta {
  /** i18next resource code — always bare, never region-suffixed. */
  code: string;
  /** Shown in the switcher, in the language's own script. */
  nativeName: string;
  /**
   * Flag emoji for the switcher.
   *
   * A flag is not a language — Spanish is not only Spain, German is not only
   * Germany — but in a grid of seventeen entries a column of names alone is a
   * wall, and a flag is the fastest thing the eye lands on. Each one is the
   * country a reader most likely associates with the language; the row is
   * still LABELLED by the language name, so nothing depends on reading the
   * flag correctly. Same choice, same flags as the public site.
   */
  flag: string;
  /** English name. The switcher shows `nativeName`; this is for
   *  admin-facing surfaces and for anything that has to name a language
   *  to someone who cannot read its own script. */
  englishName: string;
  script: LocaleScript;
  dir: 'ltr' | 'rtl';
  /**
   * BCP-47 tag for Intl.DateTimeFormat / NumberFormat.
   *
   * Two deliberate overrides: German formats as de-AT (this is a Vienna
   * brokerage — 22.08.2026, € 1.234.568), and every locale whose default
   * calendar or numbering system is not Gregorian/Latin is pinned back to
   * both. A contract date has to be readable by the agent, the seller, the
   * buyer and the notary at the same time, so fa must not render 22.08.2026
   * as ۳۱ مرداد ۱۴۰۵.
   */
  intl: string;
  /** 'live' = has a translation file and is selectable. */
  status: 'live' | 'planned';
}

/** The seventeen languages of the final list (2026-08-23), in the owner's order.
 *  Croatian was translated before the list was narrowed and kept for a while;
 *  the owner dropped it on 2026-08-24, so the locale file went with it. */
export const LOCALES: readonly LocaleMeta[] = [
  { code: 'de', flag: '🇩🇪', nativeName: 'Deutsch',    englishName: 'German',     script: 'latin',      dir: 'ltr', intl: 'de-AT',                    status: 'live' },
  { code: 'en', flag: '🇬🇧', nativeName: 'English',    englishName: 'English',    script: 'latin',      dir: 'ltr', intl: 'en-GB',                    status: 'live' },
  { code: 'sr', flag: '🇷🇸', nativeName: 'Srpski',     englishName: 'Serbian',    script: 'latin',      dir: 'ltr', intl: 'sr-Latn-RS',               status: 'live' },
  { code: 'bs', flag: '🇧🇦', nativeName: 'Bosanski',   englishName: 'Bosnian',    script: 'latin',      dir: 'ltr', intl: 'bs-Latn-BA',               status: 'live'    },
  { code: 'tr', flag: '🇹🇷', nativeName: 'Türkçe',     englishName: 'Turkish',    script: 'latin',      dir: 'ltr', intl: 'tr-TR',                    status: 'live'    },
  { code: 'ru', flag: '🇷🇺', nativeName: 'Русский',    englishName: 'Russian',    script: 'cyrillic',   dir: 'ltr', intl: 'ru-RU',                    status: 'live'    },
  { code: 'ro', flag: '🇷🇴', nativeName: 'Română',     englishName: 'Romanian',   script: 'latin',      dir: 'ltr', intl: 'ro-RO',                    status: 'live'    },
  { code: 'hu', flag: '🇭🇺', nativeName: 'Magyar',     englishName: 'Hungarian',  script: 'latin',      dir: 'ltr', intl: 'hu-HU',                    status: 'live'    },
  { code: 'pl', flag: '🇵🇱', nativeName: 'Polski',     englishName: 'Polish',     script: 'latin',      dir: 'ltr', intl: 'pl-PL',                    status: 'live'    },
  { code: 'es', flag: '🇪🇸', nativeName: 'Español',    englishName: 'Spanish',    script: 'latin',      dir: 'ltr', intl: 'es-ES',                    status: 'live'    },
  { code: 'ar', flag: '🇸🇦', nativeName: 'العربية',     englishName: 'Arabic',     script: 'arabic',     dir: 'rtl', intl: 'ar-u-ca-gregory-nu-latn',  status: 'live'    },
  { code: 'sq', flag: '🇦🇱', nativeName: 'Shqip',      englishName: 'Albanian',   script: 'latin',      dir: 'ltr', intl: 'sq-AL',                    status: 'live'    },
  { code: 'bg', flag: '🇧🇬', nativeName: 'Български',  englishName: 'Bulgarian',  script: 'cyrillic',   dir: 'ltr', intl: 'bg-BG',                    status: 'live'    },
  { code: 'he', flag: '🇮🇱', nativeName: 'עברית',       englishName: 'Hebrew',     script: 'hebrew',     dir: 'rtl', intl: 'he-IL',                    status: 'live'    },
  { code: 'zh', flag: '🇨🇳', nativeName: '中文',        englishName: 'Chinese',    script: 'han',        dir: 'ltr', intl: 'zh-CN',                    status: 'live'    },
  { code: 'sk', flag: '🇸🇰', nativeName: 'Slovenčina', englishName: 'Slovak',     script: 'latin',      dir: 'ltr', intl: 'sk-SK',                    status: 'live'    },
  { code: 'fr', flag: '🇫🇷', nativeName: 'Français',   englishName: 'French',     script: 'latin',      dir: 'ltr', intl: 'fr-FR',                    status: 'live'    },
] as const;

export const DEFAULT_LOCALE = 'de';

/**
 * Shown first in the switcher, above a hairline — the firm's main markets.
 *
 * Seventeen names in registry order is a wall to scan. Putting these six on
 * top means most people never have to read past the first two rows. Mirrors
 * the public site so the two switchers behave the same way.
 */
export const PRIMARY_LOCALES: readonly string[] = ['en', 'de', 'es', 'fr', 'ru', 'zh'];

/** Live locales, primaries first, everything else in registry order. */
export function orderedLiveLocales(): LocaleMeta[] {
  const live = liveLocales();
  const primary = PRIMARY_LOCALES.map((c) => live.find((l) => l.code === c)).filter(
    (l): l is LocaleMeta => Boolean(l),
  );
  const rest = live.filter((l) => !PRIMARY_LOCALES.includes(l.code));
  return [...primary, ...rest];
}

/** How many of the ordered list are primaries — where the divider goes. */
export function primaryCount(): number {
  const live = liveLocales();
  return PRIMARY_LOCALES.filter((c) => live.some((l) => l.code === c)).length;
}

const BY_CODE = new Map(LOCALES.map((l) => [l.code, l]));

export function localeMeta(code: string | undefined | null): LocaleMeta | undefined {
  return code ? BY_CODE.get(code) : undefined;
}

/** Only these are selectable — a language must not be offered before it exists. */
export function liveLocales(): LocaleMeta[] {
  return LOCALES.filter((l) => l.status === 'live');
}

export function isSupportedLocale(code: string | undefined | null): boolean {
  return localeMeta(code)?.status === 'live';
}

/** Falls back to German rather than throwing — a bad stored preference must
 *  never be able to leave a user with a blank app. */
export function resolveLocale(code: string | undefined | null): LocaleMeta {
  return (isSupportedLocale(code) ? localeMeta(code) : localeMeta(DEFAULT_LOCALE))!;
}

export function localeDirection(code: string | undefined | null): 'ltr' | 'rtl' {
  return resolveLocale(code).dir;
}

/** BCP-47 tag for Intl.* — never pass a raw i18next code to a formatter. */
export function intlTag(code: string | undefined | null): string {
  return resolveLocale(code).intl;
}
